/**
 * Password Setup Page
 * Reached when a user clicks the invite / password-recovery link in their email.
 *
 * Supabase puts the auth token in the URL hash (e.g. `#access_token=…&type=invite`).
 * supabase-js (detectSessionInUrl) parses that hash on load and establishes a
 * session — which is what lets `auth.updateUser({ password })` succeed here.
 *
 * The app has no router (Phase 4 uses conditional rendering), so App.tsx routes
 * to this page purely on `window.location.pathname === '/auth-setup'`, which the
 * Supabase redirect URL should point at. The hash carrying the token is consumed
 * by supabase-js, so we detect validity via the resulting session rather than by
 * re-reading the (already stripped) hash.
 */

import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../services/supabase';
import { Icons } from '../lib/icons';

const wrap: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  background: 'var(--crm-bg)',
  fontFamily: 'monospace',
};
const card: React.CSSProperties = {
  width: '100%',
  maxWidth: 400,
  padding: 32,
  background: 'var(--crm-panel)',
  borderRadius: 10,
  border: '1px solid var(--crm-line)',
  color: 'var(--crm-text)',
};
const label: React.CSSProperties = { display: 'block', margin: '12px 0 4px', color: 'var(--crm-muted)', fontSize: 12 };
const input: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: 8,
  background: 'var(--crm-panel-2)',
  color: 'var(--crm-text)',
  border: '1px solid var(--crm-line)',
  borderRadius: 4,
  fontFamily: 'monospace',
  boxSizing: 'border-box',
};
const btn: React.CSSProperties = {
  width: '100%',
  padding: '10px',
  marginTop: 16,
  background: 'var(--crm-accent-fill)',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'monospace',
};
const errorBox: React.CSSProperties = {
  color: 'var(--crm-red)',
  border: '1px solid var(--crm-red-line)',
  padding: 8,
  borderRadius: 4,
  marginTop: 12,
  fontSize: 13,
};

export function AuthSetup() {
  const [checking, setChecking] = useState(true);
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Wait for supabase-js to establish a session from the invite/recovery hash.
  useEffect(() => {
    let active = true;

    supabaseClient.auth.getSession().then(({ data }) => {
      if (active && data.session) {
        setReady(true);
        setChecking(false);
      }
    });

    const { data: sub } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      if (active && session) {
        setReady(true);
        setChecking(false);
      }
    });

    // If no session materialises shortly, the link is invalid or expired.
    const timer = setTimeout(() => {
      if (active) setChecking(false);
    }, 2500);

    return () => {
      active = false;
      sub.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError('Both password fields are required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      // The session from the invite link authenticates this update.
      const { error: updateError } = await supabaseClient.auth.updateUser({ password });
      if (updateError) throw updateError;

      // Sign out of the temporary invite session so the user logs in cleanly.
      await supabaseClient.auth.signOut();
      setSuccess(true);
      setTimeout(() => window.location.assign('/'), 2000);
    } catch (err: any) {
      setError(err?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={wrap}>
        <div style={{ ...card, textAlign: 'center' }}>
          <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}><Icons.check size={18} /> Password set</h2>
          <p style={{ color: 'var(--crm-muted)' }}>Redirecting to login…</p>
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div style={wrap}>
        <div style={{ ...card, textAlign: 'center', color: 'var(--crm-muted)' }}>Validating link…</div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h1 style={{ marginTop: 0 }}>Link invalid or expired</h1>
          <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>
            This password link is no longer valid. Ask an admin to re-send your invite.
          </p>
          <button style={btn} onClick={() => window.location.assign('/')}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <h1 style={{ marginTop: 0 }}>Set your password</h1>
        <form onSubmit={handleSetPassword}>
          <label style={label}>New password</label>
          <input
            style={input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            placeholder="Enter new password"
            autoComplete="new-password"
          />

          <label style={label}>Confirm password</label>
          <input
            style={input}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            placeholder="Confirm password"
            autoComplete="new-password"
          />

          {error && <div style={errorBox}>{error}</div>}

          <button style={{ ...btn, background: loading ? 'var(--crm-line)' : 'var(--crm-accent-fill)' }} type="submit" disabled={loading}>
            {loading ? 'Setting password…' : 'Set password'}
          </button>
        </form>
      </div>
    </div>
  );
}
